import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.click';

export default function ArticlePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleUrl = `${siteUrl}/what-is-litbuy-spreadsheet`;

  return (
    <main>
      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
              { '@type': 'ListItem', position: 2, name: 'What Is a LitBuy Spreadsheet', item: articleUrl },
            ],
          }),
        }}
      />

      <Navbar />

      {/* Article Header */}
      <section className="pt-28 pb-10 md:pt-36 md:pb-14 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-700">What Is a LitBuy Spreadsheet</span>
          </div>

          <span className="inline-block text-xs font-label font-semibold bg-accent-100 text-accent-800 px-3 py-1.5 rounded-full mb-4">
            Beginner Guide
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-tight">
            What Is a LitBuy Spreadsheet? Complete Beginner Guide
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
            Everything you need to know about litbuy spreadsheet &mdash; from basic concepts and setup 
            to advanced features and best practices. This comprehensive guide will help you master 
            the art of spreadsheet-based buying management.
          </p>

          <div className="mt-6 flex items-center gap-3 text-xs text-foreground-500">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </span>
            <span>15 min read</span>
            <span className="text-foreground-300">|</span>
            <span>Updated June 2026</span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-background-50 pb-10">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <img
            src="https://readdy.ai/api/search-image?query=Fashion%20shopper%20at%20a%20minimal%20wooden%20desk%20with%20laptop%20displaying%20an%20organized%20spreadsheet%2C%20surrounded%20by%20neatly%20folded%20garments%20in%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20natural%20daylight%20from%20window%2C%20a%20small%20potted%20plant%2C%20editorial%20lifestyle%20photography%20with%20cozy%20productive%20atmosphere&width=1200&height=600&seq=article-hero-v2-001&orientation=landscape"
            alt="LitBuy Spreadsheet Beginner Guide - workspace setup with spreadsheet interface"
            className="w-full rounded-2xl"
          />
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-6 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
              Table of Contents
            </h2>
            <ul className="space-y-2">
              {[
                'What Is a LitBuy Spreadsheet?',
                'Key Features of LitBuy Spreadsheet',
                'Benefits for Fashion Shoppers',
                'LitBuy Spreadsheet vs Traditional Methods',
                'How to Set Up Your First LitBuy Spreadsheet',
                'Common Mistakes to Avoid',
                'Best Practices and Pro Tips',
                'Is LitBuy Spreadsheet Safe?',
                'Frequently Asked Questions',
                'Conclusion and Next Steps',
              ].map((item, idx) => (
                <li key={idx}>
                  <a
                    href={`#section-${idx}`}
                    className="text-sm text-foreground-600 hover:text-primary-600 transition-colors flex items-center gap-2"
                  >
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="py-8 md:py-12 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="prose-custom space-y-12">

            {/* Section 1 */}
            <section id="section-0">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                What Is a LitBuy Spreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                A <strong>litbuy spreadsheet</strong> is a comprehensive organizational framework 
                designed to help shoppers, fashion enthusiasts, and everyday consumers manage their 
                purchasing activities in one centralized digital workspace. Unlike scattered notes, 
                mental checklists, or disorganized bookmarks, a litbuy spreadsheet consolidates 
                everything you need to know about your shopping journey into a structured, easy-to-navigate format.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                At its core, a litbuy spreadsheet functions as a personal buying assistant. It tracks 
                what you have purchased, what you plan to buy, how much you have spent, and where 
                you found the best deals. For fashion shoppers who browse multiple stores &mdash; from 
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline"> Kaspreadsheet</a> to other retailers &mdash; having 
                a centralized record prevents duplicate purchases, overspending, and the frustration 
                of losing track of orders.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                The term &ldquo;litbuy&rdquo; encapsulates the idea of making informed, efficient, and 
                satisfying purchases. When combined with spreadsheet technology, it creates a powerful 
                tool that transforms casual browsing into strategic shopping. Think of it as your 
                personal command center for all buying-related decisions.
              </p>
              <p className="text-sm text-foreground-500 mt-4">
                <Link to="/" className="text-primary-600 hover:text-primary-700 underline">← Back to LitBuy Spreadsheet Hub</Link>
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-1">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                Key Features of LitBuy Spreadsheet
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Understanding the features of a litbuy spreadsheet helps you appreciate its value. 
                Here are the core capabilities that set it apart from basic spreadsheets and manual 
                tracking methods.
              </p>

              <div className="space-y-4 mt-5">
                {[
                  {
                    title: 'Category-Based Organization',
                    desc: 'Group your purchases by category &mdash; shoes, hoodies, t-shirts, jackets, accessories, and more. This hierarchical structure mirrors how fashion stores like Kaspreadsheet organize their products, making navigation intuitive and familiar.',
                  },
                  {
                    title: 'Price Comparison Engine',
                    desc: 'Built-in formulas allow you to compare prices across different stores and time periods. Identify the best time to buy, spot price drops, and never overpay for an item again.',
                  },
                  {
                    title: 'Order Tracking Dashboard',
                    desc: 'Centralize all your order information including store name, order number, expected delivery date, and tracking links. Say goodbye to digging through email inboxes for order confirmations.',
                  },
                  {
                    title: 'Spending Analytics',
                    desc: 'Visual summaries and automatic calculations help you understand your spending patterns. See monthly totals, category breakdowns, and trends that inform smarter future purchases.',
                  },
                  {
                    title: 'Wishlist Management',
                    desc: 'Maintain a separate section for items you are considering. Rate priority levels, set price alerts, and move items to your purchased list with a single action.',
                  },
                ].map((feature, idx) => (
                  <div key={idx} className="bg-background-100 rounded-lg p-4 md:p-5 border border-background-200/60">
                    <h3 className="text-base font-label font-semibold text-foreground-900 mb-2">
                      <span className="w-5 h-5 inline-flex items-center justify-center mr-2 text-accent-600">
                        <i className="ri-check-line"></i>
                      </span>
                      {feature.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed pl-7">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 3 */}
            <section id="section-2">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                Benefits for Fashion Shoppers
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Fashion shopping presents unique challenges. With seasonal collections, size variations 
                across brands, and frequent sales events, keeping everything organized can feel 
                overwhelming. A litbuy spreadsheet addresses these challenges directly.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                For shoppers browsing stores like 
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline"> Kaspreadsheet</a>, 
                where categories range from shoes and hoodies to accessories and bags, a litbuy 
                spreadsheet becomes an indispensable companion. You can log each item you are interested 
                in, compare similar products across categories, and avoid impulse purchases by seeing 
                your full buying history at a glance.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Additional benefits include better budget management, the ability to coordinate outfits 
                by cross-referencing purchases, and the satisfaction of knowing exactly what you own 
                without rummaging through closets or scrolling through endless order histories.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                The time savings alone justify the initial setup effort. What used to take 30 minutes 
                of searching through confirmation emails can now be found in seconds.
              </p>
            </section>

            {/* Section 4 - Comparison Table */}
            <section id="section-3">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                LitBuy Spreadsheet vs Traditional Methods
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                How does a litbuy spreadsheet compare against the alternatives? See the comparison below.
              </p>

              <div className="overflow-x-auto rounded-xl border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-foreground-900 text-background-50">
                      <th className="text-left px-5 py-4 font-label font-semibold">Feature</th>
                      <th className="text-center px-5 py-4 font-label font-semibold">LitBuy Spreadsheet</th>
                      <th className="text-center px-5 py-4 font-label font-semibold">Manual Notes</th>
                      <th className="text-center px-5 py-4 font-label font-semibold">Memory Only</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Organization', 'Excellent', 'Fair', 'None'],
                      ['Searchability', 'Instant', 'Slow', 'None'],
                      ['Price Tracking', 'Automatic', 'Manual', 'None'],
                      ['History Access', 'Complete', 'Partial', 'None'],
                      ['Sharing', 'Easy', 'Hard', 'Impossible'],
                    ].map((row, idx) => (
                      <tr key={idx} className="border-b border-background-200/70 bg-background-50">
                        <td className="px-5 py-3 font-label font-medium text-foreground-800">{row[0]}</td>
                        <td className="text-center px-5 py-3 text-accent-600 font-semibold">{row[1]}</td>
                        <td className="text-center px-5 py-3 text-foreground-600">{row[2]}</td>
                        <td className="text-center px-5 py-3 text-foreground-600">{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 5 */}
            <section id="section-4">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                How to Set Up Your First LitBuy Spreadsheet
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Setting up your first litbuy spreadsheet is straightforward. Follow these steps to 
                create a functional buying management system in under 15 minutes.
              </p>

              <div className="space-y-4 mt-5">
                {[
                  { step: '01', title: 'Choose Your Platform', desc: 'Select a spreadsheet application that suits your needs. Popular options include Google Sheets for cloud access across devices, or Microsoft Excel for advanced formula capabilities. Both work perfectly for a litbuy spreadsheet setup.' },
                  { step: '02', title: 'Define Your Categories', desc: 'Create tabs or sections for each product category you shop for. Common categories include Shoes, T-Shirts, Hoodies, Jackets, Pants, Accessories, and Bags. Mirror the organization used by your favorite stores like Kaspreadsheet for consistency.' },
                  { step: '03', title: 'Create Column Headers', desc: 'Set up columns for: Item Name, Store, Price, Size, Color, Order Date, Delivery Status, Notes, and Rating. These standardized fields ensure every entry captures useful information consistently.' },
                  { step: '04', title: 'Add Formula Columns', desc: 'Implement formulas for automatic calculations. Add a running total for spending, conditional formatting for delivery status, and summary statistics per category. These automated features are what elevate a basic sheet into a true litbuy spreadsheet.' },
                  { step: '05', title: 'Populate with Existing Purchases', desc: 'Start by entering recent purchases to build your history. This initial investment creates immediate value and helps you refine your column structure before logging new items going forward.' },
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-label font-bold text-sm">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="text-base font-label font-semibold text-foreground-900 mb-1">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 6 */}
            <section id="section-5">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                Common Mistakes to Avoid
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Even experienced spreadsheet users make mistakes when setting up their litbuy spreadsheet. 
                Here are the most common pitfalls and how to avoid them.
              </p>

              <ul className="space-y-3 mt-5">
                {[
                  'Overcomplicating the structure &mdash; Start simple with essential columns and add complexity only when needed.',
                  'Forgetting to back up &mdash; Use cloud-based platforms or maintain regular local backups of your litbuy spreadsheet to prevent data loss.',
                  'Inconsistent data entry &mdash; Set clear naming conventions for stores, categories, and items from day one to maintain searchability.',
                  'Neglecting regular updates &mdash; A spreadsheet is only useful if kept current. Set a weekly reminder to log new purchases and update delivery statuses.',
                  'Ignoring the wishlist function &mdash; The wishlist tab is one of the most valuable features. Use it to delay impulse purchases and track price changes over time.',
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-3 text-sm text-foreground-700 leading-relaxed">
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-primary-500 mt-0.5">
                      <i className="ri-error-warning-line"></i>
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Section 7 */}
            <section id="section-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                Best Practices and Pro Tips
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Once you have the basics in place, these pro tips will help you get the most out of 
                your litbuy spreadsheet.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                {[
                  { icon: 'ri-palette-line', tip: 'Use color coding for delivery status (green for delivered, yellow for in transit, red for pending).' },
                  { icon: 'ri-filter-line', tip: 'Create filter views for quick access to specific categories or stores.', },
                  { icon: 'ri-bar-chart-line', tip: 'Build monthly spending charts to visualize trends and identify saving opportunities.', },
                  { icon: 'ri-link', tip: 'Add direct hyperlinks to product pages so you can revisit items with one click.', },
                ].map((item, idx) => (
                  <div key={idx} className="bg-accent-50 rounded-lg p-4 border border-accent-200/50">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-accent-100 rounded-full">
                        <i className={`${item.icon} text-accent-600 text-sm`}></i>
                      </span>
                      <p className="text-sm text-foreground-700 leading-relaxed">{item.tip}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 8 - Safety */}
            <section id="section-7">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                Is LitBuy Spreadsheet Safe?
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Safety is a common concern among new litbuy spreadsheet users. The short answer is 
                yes &mdash; a litbuy spreadsheet is safe when used with proper security practices. 
                The spreadsheet itself is simply an organizational tool; it does not have access to 
                your payment information or store accounts unless you explicitly enter that data.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                To maintain the highest level of security with your litbuy spreadsheet, follow 
                these guidelines: never store credit card numbers or passwords in the spreadsheet, 
                use a strong password for your cloud storage account, enable two-factor authentication 
                where available, and regularly review who has access to your shared files.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                For additional peace of mind, consider storing sensitive columns in a separate, 
                password-protected sheet and only linking summary data to your main litbuy spreadsheet. 
                This layered approach ensures that even in the unlikely event of unauthorized access, 
                your most sensitive information remains protected.
              </p>
            </section>

            {/* Section 9 - FAQ */}
            <section id="section-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {[
                  {
                    q: 'Do I need technical skills to use a litbuy spreadsheet?',
                    a: 'Not at all. A litbuy spreadsheet is designed to be user-friendly. If you can type in a cell and use basic copy-paste functions, you have all the skills needed. Advanced features like formulas are optional and can be added gradually as you become more comfortable.',
                  },
                  {
                    q: 'Can I share my litbuy spreadsheet with family members?',
                    a: 'Yes, cloud-based platforms like Google Sheets make sharing easy. You can grant view-only or edit access, allowing family members to contribute to shared shopping lists or coordinate group purchases.',
                  },
                  {
                    q: 'How does litbuy spreadsheet help me save money?',
                    a: 'By tracking prices over time, identifying spending patterns, and maintaining a wishlist, a litbuy spreadsheet helps you avoid impulse purchases and recognize when items are genuinely on sale. Many users report saving 15-25% on their annual shopping budget after consistent use.',
                  },
                  {
                    q: 'Where can I shop using insights from my litbuy spreadsheet?',
                    a: 'Your litbuy spreadsheet is store-agnostic, meaning it works with any retailer. Many users find that stores like Kaspreadsheet offer excellent variety across categories, making it easy to find everything from shoes and clothing to accessories in one place.',
                  },
                ].map((faq, idx) => (
                  <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/70">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <h3 className="text-sm font-label font-semibold text-foreground-800 pr-4">{faq.q}</h3>
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-45 transition-transform">
                        <i className="ri-add-line"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Section 10 - Conclusion */}
            <section id="section-9">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                Conclusion and Next Steps
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                A <strong>litbuy spreadsheet</strong> is more than just a digital notebook &mdash; it is a 
                comprehensive buying management system that empowers you to shop smarter, stay organized, 
                and make informed purchasing decisions. Whether you are managing a growing wardrobe, 
                tracking fashion trends, or simply trying to keep your online orders in order, the 
                litbuy spreadsheet framework provides the structure you need.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The key to success is consistency. Start small with a few categories, build the habit 
                of logging purchases, and gradually expand your spreadsheet as your needs evolve. 
                Within weeks, you will wonder how you ever shopped without it.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Ready to put your knowledge into practice? Visit 
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 underline"> Kaspreadsheet</a> to browse 
                the latest fashion collections across shoes, hoodies, t-shirts, jackets, pants, 
                accessories, and more. Start building your litbuy spreadsheet today and transform 
                the way you shop.
              </p>
            </section>

            {/* CTA */}
            <div className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center">
              <h3 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-3">
                Ready to Start Shopping Smarter?
              </h3>
              <p className="text-sm text-background-50/80 max-w-md mx-auto mb-6">
                Visit Kaspreadsheet now and explore thousands of fashion items across all categories.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-7 py-3 bg-background-50 text-primary-600 rounded-full font-label font-semibold text-sm hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
                >
                  litbuy spreadsheet
                  <span className="w-4 h-4 inline-flex items-center justify-center ml-2">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
                <Link
                  to="/"
                  className="px-7 py-3 border-2 border-background-50/70 text-background-50 rounded-full font-label font-semibold text-sm hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>

            {/* Internal links */}
            <div className="border-t border-background-200/70 pt-8">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                Explore More Resources
              </h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-label font-medium bg-background-100 text-foreground-700 px-4 py-2 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-home-line"></i></span>
                  LitBuy Spreadsheet Hub
                </Link>
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-1.5 text-xs font-label font-medium bg-background-100 text-foreground-700 px-4 py-2 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-shopping-bag-line"></i></span>
                  Shop Kaspreadsheet
                </a>
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-1.5 text-xs font-label font-medium bg-background-100 text-foreground-700 px-4 py-2 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap">
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
                  litbuy spreadsheet
                </a>
              </div>
            </div>

          </div>
        </div>
      </article>

      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Do I need technical skills to use a litbuy spreadsheet?',
                acceptedAnswer: { '@type': 'Answer', text: 'Not at all. A litbuy spreadsheet is designed to be user-friendly. If you can type in a cell and use basic copy-paste functions, you have all the skills needed.' },
              },
              {
                '@type': 'Question',
                name: 'Can I share my litbuy spreadsheet with family members?',
                acceptedAnswer: { '@type': 'Answer', text: 'Yes, cloud-based platforms like Google Sheets make sharing easy. You can grant view-only or edit access.' },
              },
              {
                '@type': 'Question',
                name: 'How does litbuy spreadsheet help me save money?',
                acceptedAnswer: { '@type': 'Answer', text: 'By tracking prices over time and identifying spending patterns, a litbuy spreadsheet helps you avoid impulse purchases.' },
              },
              {
                '@type': 'Question',
                name: 'Where can I shop using insights from my litbuy spreadsheet?',
                acceptedAnswer: { '@type': 'Answer', text: 'Your litbuy spreadsheet is store-agnostic. Stores like Kaspreadsheet offer excellent variety across categories.' },
              },
            ],
          }),
        }}
      />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'What Is a LitBuy Spreadsheet? Complete Beginner Guide',
            description: 'Everything you need to know about litbuy spreadsheet — from basic concepts and setup to advanced features and best practices.',
            datePublished: '2026-06-20',
            dateModified: '2026-06-20',
            author: { '@type': 'Organization', name: 'LitBuy Spreadsheet Hub' },
            mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
          }),
        }}
      />

      <Footer />
    </main>
  );
}